import {
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  Input,
  Output,
  TemplateRef,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type ListboxShape = 'default' | 'dense' | 'pill';

export type ListboxPlacement = 'below' | 'above';

const TRIGGER_SHAPE: Record<ListboxShape, string> = {
  default: 'h-11 w-full rounded-aav-sm px-[12px] text-aav-body-medium',
  dense: 'h-[38px] w-full justify-between rounded-aav-sm px-[12px] text-aav-body-medium',
  pill: 'h-8 w-auto rounded-full pl-[10px] pr-[10px] text-aav-body-medium',
};

let nextId = 0;

@Component({
  selector: 'aav-listbox',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ListboxComponent), multi: true },
  ],
  host: { class: 'relative inline-flex' },
  template: `
    <button
      type="button"
      role="combobox"
      [id]="triggerId"
      [disabled]="disabled"
      [class]="triggerClasses"
      [attr.aria-controls]="listId"
      [attr.aria-expanded]="open"
      [attr.aria-label]="ariaLabel || null"
      [attr.aria-activedescendant]="open && activeIndex >= 0 ? optionId(activeIndex) : null"
      (click)="toggle()"
      (keydown)="onKeydown($event)"
      (blur)="onTouched()"
    >
      <ng-container
        *ngTemplateOutlet="leadingTemplate; context: { $implicit: selectedOption }"
      ></ng-container>

      <span class="truncate">{{ selectedOption?.label }}</span>

      <aav-icon
        name="expand_more"
        [size]="18"
        class="shrink-0 text-aav-on-surface-variant transition-transform duration-150"
        [class.rotate-180]="open"
      ></aav-icon>
    </button>

    <label *ngIf="label" [for]="triggerId" [class]="labelClasses">{{ label }}</label>

    <ul
      *ngIf="open"
      role="listbox"
      [id]="listId"
      [attr.aria-label]="ariaLabel || null"
      [class]="listClasses"
    >
      <li
        *ngFor="let option of options; index as i; trackBy: trackByOptionValue"
        role="option"
        [id]="optionId(i)"
        [attr.aria-selected]="option.value === value"
        [attr.aria-disabled]="option.disabled ? true : null"
        [class]="optionClasses(i, option)"
        (click)="select(option)"
        (mouseenter)="onOptionHover(i, option)"
      >
        <ng-container
          *ngTemplateOutlet="leadingTemplate; context: { $implicit: option }"
        ></ng-container>

        <span class="flex-1 whitespace-nowrap">{{ option.label }}</span>

        <aav-icon
          *ngIf="option.value === value"
          name="check"
          [size]="16"
          class="shrink-0 text-aav-primary"
        ></aav-icon>
      </li>
    </ul>
  `,
})
export class ListboxComponent implements ControlValueAccessor {
  @Input() options: SelectOption[] = [];
  @Input() label = '';
  @Input() shape: ListboxShape = 'default';
  @Input() placement: ListboxPlacement = 'below';
  @Input() ariaLabel = '';
  @Input() disabled = false;

  @Input()
  set value(value: string | null) {
    this.selectedValue = value ?? '';
  }
  get value(): string {
    return this.selectedValue;
  }

  @Output() valueChange = new EventEmitter<string>();

  @ContentChild(TemplateRef) leadingTemplate: TemplateRef<unknown> | null = null;

  readonly triggerId = `aav-listbox-${nextId}`;
  readonly listId = `aav-listbox-list-${nextId++}`;

  open = false;
  activeIndex = 0;

  private selectedValue = '';

  private onChange: (value: string) => void = () => undefined;
  onTouched: () => void = () => undefined;

  get selectedOption(): SelectOption | null {
    return this.options.find((option) => option.value === this.value) ?? null;
  }

  get triggerClasses(): string {
    const base =
      'inline-flex items-center gap-2 bg-aav-surface text-aav-on-surface ' +
      'ring-1 ring-inset ring-aav-outline-variant hover:ring-aav-on-surface-variant/40 ' +
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-aav-primary focus-visible:shadow-aav-focus ' +
      'cursor-pointer transition duration-150 ' +
      'disabled:cursor-not-allowed disabled:opacity-40';
    return `${base} ${TRIGGER_SHAPE[this.shape]}`;
  }

  get labelClasses(): string {
    const base =
      'pointer-events-none absolute -top-2 left-2.5 z-10 bg-aav-surface px-[5px] ' +
      'text-aav-label-small-strong leading-[13px]';
    if (this.disabled) {
      return `${base} text-aav-on-surface-variant opacity-40`;
    }
    return this.open ? `${base} text-aav-primary` : `${base} text-aav-on-surface-variant`;
  }

  get listClasses(): string {
    const base =
      'absolute left-1/2 z-30 max-h-64 min-w-full -translate-x-1/2 overflow-auto ' +
      'rounded-aav-sm bg-aav-surface py-[4px] shadow-aav-elevation-2 ' +
      'ring-1 ring-inset ring-aav-outline-variant';
    return this.placement === 'above'
      ? `${base} bottom-full mb-[4px]`
      : `${base} top-full mt-[4px]`;
  }

  trackByOptionValue(_index: number, option: SelectOption): string {
    return option.value;
  }

  optionId(index: number): string {
    return `${this.listId}-option-${index}`;
  }

  optionClasses(index: number, option: SelectOption): string {
    const base =
      'flex items-center gap-2 px-[12px] py-[8px] text-aav-body-medium text-aav-on-surface';
    if (option.disabled) return `${base} cursor-not-allowed opacity-40`;
    if (index === this.activeIndex) return `${base} cursor-pointer bg-aav-primary/8`;
    if (option.value === this.value) return `${base} cursor-pointer bg-aav-secondary/40`;
    return `${base} cursor-pointer`;
  }

  onOptionHover(index: number, option: SelectOption): void {
    if (!option.disabled) this.activeIndex = index;
  }

  toggle(): void {
    this.open ? this.close() : this.openList();
  }

  select(option: SelectOption): void {
    if (option.disabled) return;

    this.value = option.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.close();
  }

  onKeydown(event: KeyboardEvent): void {
    const { key } = event;

    if (key === 'Escape' || key === 'Tab') {
      if (this.open && key === 'Escape') event.preventDefault();
      this.close();
      return;
    }

    if (!this.open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.openList();
      }
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.moveActive(1);
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      this.moveActive(-1);
    } else if (key === 'Home') {
      event.preventDefault();
      this.activeIndex = this.firstEnabledIndex();
    } else if (key === 'End') {
      event.preventDefault();
      this.activeIndex = this.lastEnabledIndex();
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      const option = this.options[this.activeIndex];
      if (option) this.select(option);
    }
  }

  @HostListener('click', ['$event'])
  onHostClick(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.close();
  }

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.close();
  }

  private openList(): void {
    if (this.disabled) return;
    this.open = true;
    const selected = this.options.findIndex(
      (option) => option.value === this.value && !option.disabled
    );
    this.activeIndex = selected >= 0 ? selected : this.firstEnabledIndex();
  }

  private close(): void {
    this.open = false;
  }

  private firstEnabledIndex(): number {
    return this.options.findIndex((option) => !option.disabled);
  }

  private lastEnabledIndex(): number {
    for (let i = this.options.length - 1; i >= 0; i--) {
      if (!this.options[i].disabled) return i;
    }
    return -1;
  }

  private moveActive(delta: number): void {
    const total = this.options.length;
    if (!total || this.options.every((option) => option.disabled)) return;

    let next = this.activeIndex;
    do {
      next = (next + delta + total) % total;
    } while (this.options[next]?.disabled);

    this.activeIndex = next;
  }
}
